// Workflow list with version history + schedules.
//
//   - List active workflows per merchant
//   - View version history (read-only)
//   - Add / edit / delete schedule (cron + params template)
//   - Deactivate a workflow (soft)
//
// Editing the steps themselves happens via re-recording — see the Record tab.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Paper, Typography, Stack, Button, IconButton, Chip, Tooltip,
    Table, TableHead, TableBody, TableRow, TableCell, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
    Alert, CircularProgress, Select, FormControl, InputLabel,
    Accordion, AccordionSummary, AccordionDetails, Switch,
    LinearProgress, Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import LayersIcon from '@mui/icons-material/Layers';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import PublicIcon from '@mui/icons-material/Public';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import EventIcon from '@mui/icons-material/Event';
import RecorderModal from './RecorderModal';
import {
    rpaMerchantList, rpaWorkflowList, rpaWorkflowVersions, rpaWorkflowDeactivate,
    rpaScheduleList, rpaScheduleCreate, rpaScheduleDelete, rpaScheduleUpdate,
    rpaRunEnqueue, rpaWorkflowGet, rpaWorkflowUpdateSteps,
} from './api';
import {
    KpiCard, KpiGrid, SectionPaper, TableShell, EmptyState, Toolbar,
    RPA_COLORS, formatDateLong,
} from './rpaTheme';

// =============================================================================
// Schedule helpers — visual frequency picker → cron expression + plain English.
// Users never touch cron syntax unless they pick the "Custom" advanced option.
// =============================================================================

const FREQUENCIES = [
    { value: 'daily',   label: 'Daily',   subtitle: 'Every day at a set time' },
    { value: 'weekly',  label: 'Weekly',  subtitle: 'Pick the days of the week' },
    { value: 'monthly', label: 'Monthly', subtitle: 'On a chosen day of the month' },
    { value: 'hourly',  label: 'Hourly',  subtitle: 'Every N hours' },
    { value: 'custom',  label: 'Custom',  subtitle: 'Cron expression (advanced)' },
];

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_LABELS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Smart date tokens the backend understands (services/rpa/.../RPAScheduleTick.py).
// Surfaced in the params builder as one-click choices for date-typed params.
const DATE_TOKENS = [
    { value: '{{today}}',             label: 'Today' },
    { value: '{{yesterday}}',         label: 'Yesterday' },
    { value: '{{prev_week_start}}',   label: 'Start of last week (Mon)' },
    { value: '{{prev_week_end}}',     label: 'End of last week (Sun)' },
    { value: '{{prev_month_start}}',  label: 'Start of last month' },
    { value: '{{prev_month_end}}',    label: 'End of last month' },
    { value: '{{this_month_start}}',  label: 'Start of this month' },
];

// Build a cron expression from the visual picker state.
function buildCronExpr(state) {
    const [hh, mm] = (state.time || '02:00').split(':').map((n) => parseInt(n, 10));
    const minute = isNaN(mm) ? 0 : mm;
    const hour = isNaN(hh) ? 2 : hh;

    if (state.frequency === 'daily') {
        return `${minute} ${hour} * * *`;
    }
    if (state.frequency === 'weekly') {
        const days = (state.daysOfWeek && state.daysOfWeek.length > 0)
            ? [...state.daysOfWeek].sort().join(',')
            : '1';
        return `${minute} ${hour} * * ${days}`;
    }
    if (state.frequency === 'monthly') {
        const dom = parseInt(state.dayOfMonth, 10) || 1;
        return `${minute} ${hour} ${Math.min(31, Math.max(1, dom))} * *`;
    }
    if (state.frequency === 'hourly') {
        const interval = Math.min(23, Math.max(1, parseInt(state.hourInterval, 10) || 6));
        return `0 */${interval} * * *`;
    }
    return (state.customCron || '').trim();
}

// Reverse: turn a saved cron expression into a visual builder state. Best-effort —
// patterns we recognise round-trip cleanly; anything else falls into 'custom'.
function parseCronToState(cron) {
    const fallback = {
        frequency: 'custom', time: '02:00',
        daysOfWeek: [1], dayOfMonth: 1, hourInterval: 6,
        customCron: cron || '',
    };
    if (!cron) return fallback;
    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 5) return fallback;
    const [m, h, dom, mon, dow] = parts;

    const intOr = (s, def) => {
        const n = parseInt(s, 10);
        return Number.isFinite(n) ? n : def;
    };
    const time = `${String(intOr(h, 2)).padStart(2, '0')}:${String(intOr(m, 0)).padStart(2, '0')}`;

    // Daily: m h * * *
    if (dom === '*' && mon === '*' && dow === '*') {
        return { ...fallback, frequency: 'daily', time };
    }
    // Weekly: m h * * dow  (dow may be a comma-list 1,3,5)
    if (dom === '*' && mon === '*' && /^[0-7](,[0-7])*$/.test(dow)) {
        const days = dow.split(',').map((d) => parseInt(d, 10) % 7);
        return { ...fallback, frequency: 'weekly', time, daysOfWeek: days };
    }
    // Monthly: m h <dom> * *
    if (mon === '*' && dow === '*' && /^\d+$/.test(dom)) {
        return { ...fallback, frequency: 'monthly', time,
                 dayOfMonth: parseInt(dom, 10) };
    }
    // Hourly: 0 */N * * *
    if (m === '0' && /^\*\/\d+$/.test(h) && dom === '*' && mon === '*' && dow === '*') {
        return { ...fallback, frequency: 'hourly',
                 hourInterval: parseInt(h.slice(2), 10) };
    }
    return fallback;
}

// Plain-English description for a builder state. Used both as a live preview
// and as the saved-schedules row label so users never see raw cron unless
// they're in advanced mode.
function describeSchedule(state) {
    const time = state.time || '02:00';
    if (state.frequency === 'daily') return `Every day at ${time}`;
    if (state.frequency === 'weekly') {
        const days = state.daysOfWeek || [];
        if (days.length === 0) return `Weekly at ${time} (no days picked)`;
        if (days.length === 7) return `Every day at ${time}`;
        const names = days.slice().sort().map((d) => DAY_LABELS_LONG[d % 7]);
        return `Every ${names.join(', ')} at ${time}`;
    }
    if (state.frequency === 'monthly') {
        return `On the ${ordinal(state.dayOfMonth || 1)} of every month at ${time}`;
    }
    if (state.frequency === 'hourly') {
        return `Every ${state.hourInterval || 6} hour${(state.hourInterval || 6) === 1 ? '' : 's'}, on the hour`;
    }
    return state.customCron ? `Custom: ${state.customCron}` : 'Custom schedule';
}

function ordinal(n) {
    const v = n % 100;
    if (v >= 11 && v <= 13) return `${n}th`;
    switch (n % 10) {
        case 1: return `${n}st`;
        case 2: return `${n}nd`;
        case 3: return `${n}rd`;
        default: return `${n}th`;
    }
}

// "in 3 hours" / "2 days ago"
function relativeTime(iso) {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    const diff = t - Date.now();
    const abs = Math.abs(diff);
    const min = Math.round(abs / 60000);
    const hr = Math.round(abs / 3600000);
    const dy = Math.round(abs / 86400000);
    let label;
    if (min < 1) label = 'just now';
    else if (min < 60) label = `${min} min`;
    else if (hr < 24) label = `${hr} hour${hr === 1 ? '' : 's'}`;
    else label = `${dy} day${dy === 1 ? '' : 's'}`;
    return diff >= 0 ? `in ${label}` : `${label} ago`;
}

const EMPTY_BUILDER = {
    frequency: 'monthly',
    time: '02:00',
    daysOfWeek: [1],
    dayOfMonth: 1,
    hourInterval: 6,
    customCron: '',
};

export default function WorkflowList() {
    const [merchants, setMerchants] = useState([]);
    const [merchantFilter, setMerchantFilter] = useState('');
    const [workflows, setWorkflows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [scheduleModal, setScheduleModal] = useState(null);  // workflow obj
    const [versionsModal, setVersionsModal] = useState(null);  // workflow obj
    const [stepsModal, setStepsModal] = useState(null);        // workflow obj
    const [duplicateOpen, setDuplicateOpen] = useState(false);
    const [duplicateMerchantId, setDuplicateMerchantId] = useState(null);
    const [duplicateSeedSteps, setDuplicateSeedSteps] = useState([]);

    const loadMerchants = useCallback(async () => {
        try {
            const r = await rpaMerchantList({ per_page: 100 });
            if (r?.data?.success) setMerchants(r.data.data || []);
        } catch { /* ignored — search panel still works */ }
    }, []);

    const loadWorkflows = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await rpaWorkflowList({
                merchant_id: merchantFilter || undefined,
                per_page: 100,
            });
            if (r?.data?.success) setWorkflows(r.data.data || []);
            else setError(r?.data?.message || 'Failed to load workflows');
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [merchantFilter]);

    useEffect(() => { loadMerchants(); }, [loadMerchants]);
    useEffect(() => { loadWorkflows(); }, [loadWorkflows]);

    const stats = useMemo(() => {
        const active = workflows.filter((w) => w.is_active);
        const totalSteps = workflows.reduce((s, w) => s + (w.step_count || 0), 0);
        const merchantsUsed = new Set(active.map((w) => w.merchant_id)).size;
        return {
            active: active.length,
            total: workflows.length,
            steps: totalSteps,
            merchants: merchantsUsed,
        };
    }, [workflows]);

    const onDeactivate = async (wf) => {
        if (!window.confirm(`Deactivate workflow "${wf.name}" v${wf.version}?`)) return;
        try {
            await rpaWorkflowDeactivate(wf.id);
            loadWorkflows();
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    const onDuplicate = async (wf) => {
        try {
            const r = await rpaWorkflowGet(wf.id);
            if (!r?.data?.success) throw new Error(r?.data?.message || 'Load failed');
            const full = r.data.data;
            setDuplicateMerchantId(full.merchant_id);
            setDuplicateSeedSteps(full.steps_json || []);
            setDuplicateOpen(true);
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    const onRunNow = async (wf) => {
        const params = {};
        // If the workflow declares params, prompt the user for each.
        const schema = wf.params_schema || {};
        for (const key of Object.keys(schema)) {
            const v = window.prompt(`Value for ${key}?`);
            if (v === null) return; // cancelled
            params[key] = v;
        }
        try {
            const r = await rpaRunEnqueue({
                workflow_id: wf.id,
                params,
                idempotency_key: `manual-${Date.now()}-${wf.id}`,
            });
            if (r?.data?.success) {
                window.alert(`Queued run #${r.data.data.id}. Check the Runs tab for progress.`);
            } else {
                setError(r?.data?.message || 'Enqueue failed');
            }
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    const merchantName = (id) => merchants.find((m) => m.id === id)?.name || `#${id}`;

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <KpiGrid>
                <KpiCard
                    label="Active workflows"
                    value={stats.active}
                    icon={<AutoAwesomeMotionIcon fontSize="small" />}
                    accent={RPA_COLORS.accent}
                />
                <KpiCard
                    label="Merchants covered"
                    value={stats.merchants}
                    icon={<LayersIcon fontSize="small" />}
                    accent={RPA_COLORS.robot}
                />
                <KpiCard
                    label="Total steps"
                    value={stats.steps}
                    icon={<EventRepeatIcon fontSize="small" />}
                    accent={RPA_COLORS.info}
                    hint="across all versions"
                />
                <KpiCard
                    label="Versions tracked"
                    value={stats.total}
                    icon={<HistoryIcon fontSize="small" />}
                    accent={RPA_COLORS.neutral}
                />
            </KpiGrid>

            <SectionPaper>
                <Toolbar>
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                        <InputLabel>Merchant</InputLabel>
                        <Select label="Merchant" value={merchantFilter}
                                onChange={(e) => setMerchantFilter(e.target.value)}>
                            <MenuItem value="">All merchants</MenuItem>
                            {merchants.map((m) => (
                                <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <IconButton onClick={loadWorkflows} disabled={loading} size="small">
                        {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                    </IconButton>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted }}>
                        Record new workflows in the <strong>Record</strong> tab.
                    </Typography>
                </Toolbar>

                <TableShell>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Workflow</TableCell>
                                <TableCell>Merchant</TableCell>
                                <TableCell>Version</TableCell>
                                <TableCell>Steps</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {workflows.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ p: 0, border: 'none' }}>
                                        <EmptyState
                                            icon={<AutoAwesomeMotionIcon />}
                                            title="No workflows yet"
                                            message="Record your first workflow to teach the agent how to log into a merchant portal and pull invoices."
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                            {workflows.map((wf) => (
                            <TableRow key={wf.id} hover>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{wf.name}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption">{merchantName(wf.merchant_id)}</Typography>
                                </TableCell>
                                <TableCell>v{wf.version}</TableCell>
                                <TableCell>{wf.step_count}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={wf.is_active ? 'Active' : 'Inactive'}
                                        size="small"
                                        color={wf.is_active ? 'primary' : 'default'}
                                        variant={wf.is_active ? 'filled' : 'outlined'}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    {wf.is_active && (
                                        <Tooltip title="Run now">
                                            <IconButton size="small" color="primary" onClick={() => onRunNow(wf)}>
                                                <PlayArrowIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="View captured steps">
                                        <IconButton size="small" onClick={() => setStepsModal(wf)}>
                                            <FormatListNumberedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Schedules">
                                        <IconButton size="small" onClick={() => setScheduleModal(wf)}>
                                            <ScheduleIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Duplicate as new workflow">
                                        <IconButton size="small" onClick={() => onDuplicate(wf)}>
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Version history">
                                        <IconButton size="small" onClick={() => setVersionsModal(wf)}>
                                            <HistoryIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    {wf.is_active && (
                                        <Tooltip title="Deactivate">
                                            <IconButton size="small" color="warning" onClick={() => onDeactivate(wf)}>
                                                <VisibilityOffIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                </TableShell>
            </SectionPaper>

            <ScheduleDialog workflow={scheduleModal} onClose={() => setScheduleModal(null)} />
            <VersionsDialog workflow={versionsModal} onClose={() => setVersionsModal(null)} />
            <StepsDialog workflow={stepsModal} onClose={() => setStepsModal(null)} />

            {/* Duplicate workflow recorder — pre-seeded with the source workflow's steps. */}
            <RecorderModal
                open={duplicateOpen}
                onClose={() => setDuplicateOpen(false)}
                merchants={merchants}
                defaultMerchantId={duplicateMerchantId}
                seedSteps={duplicateSeedSteps}
                onSaved={() => { setDuplicateOpen(false); loadWorkflows(); }}
            />
        </Box>
    );
}


function ScheduleDialog({ workflow, onClose }) {
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Builder state — visual frequency picker + time + days etc.
    const [builder, setBuilder] = useState(EMPTY_BUILDER);
    // Params state — keyed by schema field name
    const [params, setParams] = useState({});
    // null = creating new; otherwise the schedule id being edited
    const [editingId, setEditingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const cronExpr = useMemo(() => buildCronExpr(builder), [builder]);
    const description = useMemo(() => describeSchedule(builder), [builder]);

    const paramsSchema = workflow?.params_schema || null;

    const load = useCallback(async () => {
        if (!workflow) return;
        setLoading(true);
        setError(null);
        try {
            const r = await rpaScheduleList(workflow.id);
            if (r?.data?.success) setSchedules(r.data.data || []);
            else setError(r?.data?.message);
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [workflow]);

    useEffect(() => { load(); }, [load]);

    // Reset the builder when the dialog opens or the workflow changes.
    useEffect(() => {
        if (!workflow) return;
        setBuilder(EMPTY_BUILDER);
        setEditingId(null);
        // Seed params with empty values matching the schema.
        const seed = {};
        for (const key of Object.keys(paramsSchema || {})) seed[key] = '';
        setParams(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workflow?.id]);

    const startEdit = (s) => {
        setEditingId(s.id);
        setBuilder(parseCronToState(s.cron_expr));
        setParams({ ...(paramsSchema || {}) ? Object.fromEntries(
            Object.keys(paramsSchema || {}).map((k) => [k, (s.params_template || {})[k] ?? ''])
        ) : (s.params_template || {}) });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setBuilder(EMPTY_BUILDER);
        const seed = {};
        for (const key of Object.keys(paramsSchema || {})) seed[key] = '';
        setParams(seed);
    };

    const persist = async () => {
        const cron = cronExpr;
        if (!cron) { setError('Pick a frequency or enter a cron expression.'); return; }
        if (builder.frequency === 'weekly' && (builder.daysOfWeek || []).length === 0) {
            setError('Pick at least one day of the week.');
            return;
        }
        // Drop empty params so the backend gets a clean object.
        const cleanedParams = Object.fromEntries(
            Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined)
        );
        const payload = {
            cron_expr: cron,
            params_template: Object.keys(cleanedParams).length ? cleanedParams : null,
        };

        setSubmitting(true);
        setError(null);
        try {
            if (editingId) {
                await rpaScheduleUpdate(workflow.id, editingId, payload);
            } else {
                await rpaScheduleCreate(workflow.id, payload);
            }
            cancelEdit();
            load();
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (s) => {
        if (!window.confirm(`Delete this schedule? (${describeSchedule(parseCronToState(s.cron_expr))})`)) return;
        try {
            await rpaScheduleDelete(workflow.id, s.id);
            if (editingId === s.id) cancelEdit();
            load();
        } catch (e) { setError(e?.response?.data?.message || e.message); }
    };

    const toggle = async (s) => {
        try {
            await rpaScheduleUpdate(workflow.id, s.id, { is_active: !s.is_active });
            load();
        } catch (e) { setError(e?.response?.data?.message || e.message); }
    };

    return (
        <Dialog open={!!workflow} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <ScheduleIcon sx={{ color: RPA_COLORS.robot }} />
                    <Typography variant="h6">
                        Schedule — {workflow?.name}
                        <Typography component="span" variant="caption"
                                    sx={{ ml: 1, color: RPA_COLORS.textMuted }}>
                            v{workflow?.version}
                        </Typography>
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

                {/* Existing schedules */}
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    Existing schedules
                </Typography>
                {loading && <LinearProgress sx={{ mb: 2 }} />}
                {!loading && schedules.length === 0 && (
                    <Box sx={{ py: 2, textAlign: 'center', color: RPA_COLORS.textMuted }}>
                        <Typography variant="body2">
                            No schedules yet. Build one below to start automated runs.
                        </Typography>
                    </Box>
                )}
                <Stack spacing={1} sx={{ mb: 3 }}>
                    {schedules.map((s) => {
                        const desc = describeSchedule(parseCronToState(s.cron_expr));
                        const editing = editingId === s.id;
                        return (
                            <Paper key={s.id} variant="outlined"
                                   sx={{ p: 1.5,
                                         borderColor: editing ? RPA_COLORS.accent : undefined,
                                         background: editing ? RPA_COLORS.accentSoft : undefined }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }}
                                       spacing={1} alignItems={{ sm: 'center' }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                {desc}
                                            </Typography>
                                            {!s.is_active && (
                                                <Chip size="small" label="Paused" />
                                            )}
                                        </Stack>
                                        <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }} flexWrap="wrap">
                                            <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted }}>
                                                <strong>Next run:</strong>{' '}
                                                {s.next_run_at
                                                    ? `${relativeTime(s.next_run_at)}`
                                                    : 'computing…'}
                                            </Typography>
                                            {s.last_run_at && (
                                                <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted }}>
                                                    <strong>Last run:</strong> {relativeTime(s.last_run_at)}
                                                </Typography>
                                            )}
                                            <Typography variant="caption"
                                                        sx={{ color: RPA_COLORS.textFaint, fontFamily: 'monospace' }}>
                                                {s.cron_expr}
                                            </Typography>
                                        </Stack>
                                        {s.params_template && Object.keys(s.params_template).length > 0 && (
                                            <Box sx={{ mt: 0.75 }}>
                                                {Object.entries(s.params_template).map(([k, v]) => (
                                                    <Chip
                                                        key={k}
                                                        size="small"
                                                        sx={{ mr: 0.5, fontSize: 11 }}
                                                        label={`${k}: ${v}`}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <Tooltip title={s.is_active ? 'Pause' : 'Resume'}>
                                            <Switch checked={!!s.is_active} onChange={() => toggle(s)} size="small" />
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                            <IconButton size="small" onClick={() => startEdit(s)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton size="small" color="error" onClick={() => remove(s)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                            </Paper>
                        );
                    })}
                </Stack>

                <Divider sx={{ mb: 3 }} />

                {/* Builder */}
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                    {editingId ? 'Edit schedule' : 'Add new schedule'}
                </Typography>

                <ScheduleBuilder value={builder} onChange={setBuilder} />

                {/* Params */}
                {paramsSchema && Object.keys(paramsSchema).length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                            Parameters for each run
                        </Typography>
                        <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted, display: 'block', mb: 1.5 }}>
                            These values are passed to the workflow every time it fires.
                            Pick a smart token to have the agent compute the date relative to the run time.
                        </Typography>
                        <ParamsBuilder
                            schema={paramsSchema}
                            value={params}
                            onChange={setParams}
                        />
                    </Box>
                )}

                {/* Live preview */}
                <Paper variant="outlined" sx={{
                    mt: 3, p: 2,
                    background: RPA_COLORS.surfaceMuted,
                    borderColor: RPA_COLORS.accentBorder,
                }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <ScheduleIcon fontSize="small" sx={{ color: RPA_COLORS.accent }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{description}</Typography>
                    </Stack>
                    <Typography variant="caption" sx={{
                        color: RPA_COLORS.textFaint,
                        fontFamily: 'monospace',
                        mt: 0.5, display: 'block',
                    }}>
                        cron · {cronExpr || '(none)'}
                    </Typography>
                </Paper>

            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                {editingId && (
                    <Button onClick={cancelEdit}>Cancel edit</Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose}>Close</Button>
                <Button
                    variant="contained" onClick={persist}
                    disabled={submitting || !cronExpr}
                    sx={{ textTransform: 'none', fontWeight: 600,
                          background: `linear-gradient(135deg, ${RPA_COLORS.robot}, ${RPA_COLORS.accent})` }}
                >
                    {submitting ? 'Saving…' :
                        editingId ? 'Save changes' : 'Add schedule'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}


// ---- ScheduleBuilder ------------------------------------------------------

function ScheduleBuilder({ value, onChange }) {
    const set = (patch) => onChange({ ...value, ...patch });
    const toggleDay = (dow) => {
        const current = new Set(value.daysOfWeek || []);
        if (current.has(dow)) current.delete(dow);
        else current.add(dow);
        set({ daysOfWeek: Array.from(current) });
    };

    return (
        <Stack spacing={2}>
            {/* Frequency cards */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
                gap: 1,
            }}>
                {FREQUENCIES.map((f) => {
                    const selected = value.frequency === f.value;
                    return (
                        <Paper
                            key={f.value}
                            variant="outlined"
                            onClick={() => set({ frequency: f.value })}
                            sx={{
                                p: 1.25,
                                cursor: 'pointer',
                                textAlign: 'center',
                                borderColor: selected ? RPA_COLORS.accent : undefined,
                                background: selected ? RPA_COLORS.accentSoft : undefined,
                                transition: 'all 120ms',
                                '&:hover': { borderColor: RPA_COLORS.accent },
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {f.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted }}>
                                {f.subtitle}
                            </Typography>
                        </Paper>
                    );
                })}
            </Box>

            {/* Frequency-specific controls */}
            {value.frequency !== 'custom' && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                    {(value.frequency === 'daily' || value.frequency === 'weekly' ||
                        value.frequency === 'monthly') && (
                        <TextField
                            type="time" size="small" label="At"
                            value={value.time}
                            onChange={(e) => set({ time: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 140 }}
                        />
                    )}

                    {value.frequency === 'weekly' && (
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted, mb: 0.5, display: 'block' }}>
                                Days of week
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                {DAY_LABELS.map((d, i) => {
                                    const active = (value.daysOfWeek || []).includes(i);
                                    return (
                                        <Chip
                                            key={i}
                                            label={d}
                                            color={active ? 'primary' : 'default'}
                                            variant={active ? 'filled' : 'outlined'}
                                            onClick={() => toggleDay(i)}
                                            size="small"
                                            sx={{ minWidth: 44, fontWeight: 600 }}
                                        />
                                    );
                                })}
                            </Stack>
                        </Box>
                    )}

                    {value.frequency === 'monthly' && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="body2">On the</Typography>
                            <TextField
                                type="number" size="small"
                                value={value.dayOfMonth}
                                onChange={(e) => set({ dayOfMonth: e.target.value })}
                                inputProps={{ min: 1, max: 31, style: { width: 50, textAlign: 'center' } }}
                            />
                            <Typography variant="body2">of every month</Typography>
                        </Stack>
                    )}

                    {value.frequency === 'hourly' && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="body2">Every</Typography>
                            <TextField
                                type="number" size="small"
                                value={value.hourInterval}
                                onChange={(e) => set({ hourInterval: e.target.value })}
                                inputProps={{ min: 1, max: 23, style: { width: 50, textAlign: 'center' } }}
                            />
                            <Typography variant="body2">hour(s), on the hour</Typography>
                        </Stack>
                    )}
                </Stack>
            )}

            {value.frequency === 'custom' && (
                <Box>
                    <TextField
                        fullWidth size="small"
                        label="Cron expression"
                        value={value.customCron}
                        onChange={(e) => set({ customCron: e.target.value })}
                        placeholder="m h dom mon dow — e.g. 30 1 * * 1-5"
                        sx={{ fontFamily: 'monospace' }}
                    />
                    <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted, mt: 0.5, display: 'block' }}>
                        Five fields: minute · hour · day-of-month · month · day-of-week.
                    </Typography>
                </Box>
            )}
        </Stack>
    );
}


// ---- ParamsBuilder --------------------------------------------------------
// Schema-aware: every field declared in workflow.params_schema becomes a row
// with a smart-token dropdown (Today / Yesterday / Start of last month, etc.)
// AND an optional free-form input for static or custom values.

function ParamsBuilder({ schema, value, onChange }) {
    const setField = (key, v) => onChange({ ...value, [key]: v });

    return (
        <Stack spacing={1.5}>
            {Object.entries(schema || {}).map(([key, spec]) => {
                // spec may be a string ('date') or an object ({type:'date', required:true})
                const rawType = typeof spec === 'string' ? spec :
                                 (spec && spec.type) || 'string';
                const type = String(rawType).toLowerCase();
                const isDate = type === 'date';
                const cur = value[key] ?? '';
                const isToken = typeof cur === 'string' && cur.startsWith('{{') && cur.endsWith('}}');

                return (
                    <Paper key={key} variant="outlined" sx={{ p: 1.25 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                            <Box sx={{ minWidth: 160 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{key}</Typography>
                                <Typography variant="caption" sx={{ color: RPA_COLORS.textFaint }}>
                                    {isDate ? 'Date' : type}
                                </Typography>
                            </Box>

                            {isDate ? (
                                <Stack direction="row" spacing={1} sx={{ flex: 1 }} flexWrap="wrap">
                                    <FormControl size="small" sx={{ minWidth: 240 }}>
                                        <InputLabel>Smart value</InputLabel>
                                        <Select
                                            label="Smart value"
                                            value={isToken ? cur : ''}
                                            onChange={(e) => setField(key, e.target.value)}
                                        >
                                            <MenuItem value=""><em>Pick a token…</em></MenuItem>
                                            {DATE_TOKENS.map((t) => (
                                                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Typography variant="caption" sx={{
                                        color: RPA_COLORS.textFaint, alignSelf: 'center',
                                    }}>
                                        — or —
                                    </Typography>
                                    <TextField
                                        size="small" type="date" label="Static date"
                                        value={!isToken ? cur : ''}
                                        InputLabelProps={{ shrink: true }}
                                        onChange={(e) => setField(key, e.target.value)}
                                        sx={{ width: 180 }}
                                    />
                                </Stack>
                            ) : (
                                <TextField
                                    size="small" sx={{ flex: 1 }}
                                    value={cur}
                                    onChange={(e) => setField(key, e.target.value)}
                                    placeholder="Value passed to the workflow each run"
                                />
                            )}
                        </Stack>
                    </Paper>
                );
            })}
        </Stack>
    );
}


function VersionsDialog({ workflow, onClose }) {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!workflow) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        rpaWorkflowVersions(workflow.id).then((r) => {
            if (cancelled) return;
            if (r?.data?.success) setVersions(r.data.data || []);
            else setError(r?.data?.message || 'Failed to load versions');
        }).catch((e) => !cancelled && setError(e?.response?.data?.message || e.message))
          .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [workflow]);

    return (
        <Dialog open={!!workflow} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Version history — {workflow?.name}</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {loading && <CircularProgress size={20} />}
                {!loading && versions.map((v) => (
                    <Accordion key={v.id} disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>v{v.version}</Typography>
                                <Chip label={`${v.step_count} steps`} size="small" />
                                {v.is_active ? <Chip label="Active" size="small" color="primary" /> : null}
                                <Box sx={{ flex: 1 }} />
                                <Typography variant="caption" color="text.secondary">
                                    {formatDate(v.created_at)}
                                </Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                params_schema: {v.params_schema ? JSON.stringify(v.params_schema, null, 2) : '—'}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}


function formatDate(iso) {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}


// =============================================================================
// StepsDialog — view the captured steps of any workflow.
//
// Pulls the full workflow via rpaWorkflowGet (which includes steps_json),
// then renders each step with its action, selector, value, and any
// per-step date-filter recipe — exactly what the agent will execute on
// the next run.
// =============================================================================

const STEP_ACTION_LABELS = {
    navigate: 'Navigate to URL',
    click:    'Click element',
    type:     'Type into field',
    select:   'Select dropdown option',
    submit:   'Submit form',
    wait:     'Wait',
    download: 'Click + download file',
};

const STEP_ACTION_ICONS = {
    navigate: <PublicIcon fontSize="small" />,
    click:    <ArrowForwardIosIcon fontSize="small" />,
    type:     <KeyboardIcon fontSize="small" />,
    select:   <ArrowForwardIosIcon fontSize="small" />,
    submit:   <SendIcon fontSize="small" />,
    wait:     <HourglassBottomIcon fontSize="small" />,
    download: <DownloadIcon fontSize="small" />,
};

function StepsDialog({ workflow, onClose }) {
    const [full, setFull] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Step the user clicked Delete on, awaiting confirmation. Stores
    // both the index into the visible array and a snapshot of the
    // step (so the confirmation dialog can show what's about to go).
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!workflow) {
            setFull(null);
            setError(null);
            setPendingDelete(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        setFull(null);
        setPendingDelete(null);
        rpaWorkflowGet(workflow.id).then((r) => {
            if (cancelled) return;
            if (r?.data?.success) setFull(r.data.data);
            else setError(r?.data?.message || 'Failed to load workflow');
        }).catch((e) => {
            if (!cancelled) setError(e?.response?.data?.message || e.message);
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [workflow]);

    const steps = (full?.steps_json) || [];

    const renderValue = (s) => {
        const v = s.value;
        if (v === undefined || v === null || v === '') return null;
        const str = String(v);
        // Redact the password placeholder for display (still saved as
        // a placeholder in the workflow JSON — runtime substitutes it
        // with the merchant's stored password at run time).
        if (str.includes('{{__password__}}')) {
            return '••••••• (password placeholder)';
        }
        return str.length > 120 ? str.slice(0, 120) + '…' : str;
    };

    const confirmDelete = async () => {
        if (pendingDelete == null || !full) return;
        setDeleting(true);
        setError(null);
        try {
            // Drop the step at the pending index. Send the rest to
            // the backend, which re-indexes step_idx contiguously.
            const remaining = steps.filter(
                (_, i) => i !== pendingDelete.index,
            );
            const r = await rpaWorkflowUpdateSteps(workflow.id, remaining);
            if (!r?.data?.success) {
                throw new Error(r?.data?.message || 'Update failed');
            }
            // Refresh from the response so step_idx values are
            // exactly what the backend just persisted.
            setFull(r.data.data);
            setPendingDelete(null);
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Dialog open={!!workflow} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <FormatListNumberedIcon sx={{ color: RPA_COLORS.accent }} />
                    <Typography variant="h6" sx={{ flex: 1 }}>
                        Captured steps — {workflow?.name}
                        <Typography component="span" variant="caption"
                                    sx={{ ml: 1, color: RPA_COLORS.textMuted }}>
                            v{workflow?.version}
                        </Typography>
                    </Typography>
                    {full && (
                        <Chip label={`${steps.length} step${steps.length === 1 ? '' : 's'}`}
                              size="small" />
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                {loading && (
                    <Stack alignItems="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                    </Stack>
                )}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {!loading && !error && steps.length === 0 && (
                    <Box sx={{ py: 3, textAlign: 'center', color: RPA_COLORS.textMuted }}>
                        <Typography variant="body2">
                            This workflow has no captured steps. Re-record it
                            from the Record tab to capture the user actions.
                        </Typography>
                    </Box>
                )}
                {!loading && !error && steps.length > 0 && (
                    <Stack spacing={1}>
                        {steps.map((s, idx) => {
                            const actionLabel = STEP_ACTION_LABELS[s.action] || s.action;
                            const actionIcon = STEP_ACTION_ICONS[s.action] || null;
                            const primary = s.selector?.primary
                                            || s.selector_used
                                            || (typeof s.selector === 'string' ? s.selector : '');
                            const xpath = s.selector?.xpath;
                            const text = s.selector?.text;
                            const valDisplay = renderValue(s);
                            const df = s.date_filter;
                            return (
                                <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Chip size="small"
                                              label={`#${(s.step_idx ?? idx) + 1}`}
                                              sx={{ minWidth: 44, fontWeight: 700 }} />
                                        {actionIcon}
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {actionLabel}
                                        </Typography>
                                        {df && df.preset && (
                                            <Chip size="small" color="info" variant="outlined"
                                                  icon={<EventIcon fontSize="small" />}
                                                  label={`Date filter: ${df.preset}`}
                                                  sx={{ height: 22, fontSize: 11 }} />
                                        )}
                                        {s.semantic_label && (
                                            <Chip size="small" variant="outlined"
                                                  label={s.semantic_label}
                                                  sx={{ height: 22, fontSize: 11 }} />
                                        )}
                                        <Box sx={{ flex: 1 }} />
                                        <Tooltip title="Delete this step">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => setPendingDelete({ index: idx, step: s })}
                                                    disabled={deleting}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                    {primary && (
                                        <Typography variant="caption" sx={{
                                            display: 'block',
                                            mt: 0.75, ml: 6,
                                            fontFamily: 'monospace',
                                            color: RPA_COLORS.textMuted,
                                            wordBreak: 'break-all',
                                        }}>
                                            <strong>selector:</strong> {primary}
                                        </Typography>
                                    )}
                                    {xpath && xpath !== primary && (
                                        <Typography variant="caption" sx={{
                                            display: 'block',
                                            ml: 6,
                                            fontFamily: 'monospace',
                                            color: RPA_COLORS.textFaint,
                                            wordBreak: 'break-all',
                                        }}>
                                            <strong>xpath:</strong> {xpath}
                                        </Typography>
                                    )}
                                    {text && (
                                        <Typography variant="caption" sx={{
                                            display: 'block',
                                            ml: 6,
                                            color: RPA_COLORS.textFaint,
                                        }}>
                                            <strong>text:</strong> "{text}"
                                        </Typography>
                                    )}
                                    {valDisplay !== null && (
                                        <Typography variant="caption" sx={{
                                            display: 'block',
                                            ml: 6, mt: 0.5,
                                            color: RPA_COLORS.textMuted,
                                            wordBreak: 'break-all',
                                        }}>
                                            <strong>value:</strong> {valDisplay}
                                        </Typography>
                                    )}
                                    {df && df.preset && (
                                        <Typography variant="caption" sx={{
                                            display: 'block',
                                            ml: 6, mt: 0.5,
                                            color: RPA_COLORS.info,
                                        }}>
                                            <strong>at run time:</strong>{' '}
                                            recipe <code>{df.preset}</code>
                                            {df.format ? ` (formatted as ${df.format})` : ''}
                                            {df.preset === 'n_days_ago' && df.offset_value
                                                ? `, ${df.offset_value} ${df.offset_unit || 'days'} ago`
                                                : ''}
                                            {df.preset === 'literal' && df.literal
                                                ? ` → "${df.literal}"`
                                                : ''}
                                        </Typography>
                                    )}
                                </Paper>
                            );
                        })}
                    </Stack>
                )}

                {full?.params_schema && Object.keys(full.params_schema).length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                            Run-time parameters
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Typography variant="caption" sx={{
                                fontFamily: 'monospace',
                                color: RPA_COLORS.textMuted,
                                whiteSpace: 'pre-wrap',
                            }}>
                                {JSON.stringify(full.params_schema, null, 2)}
                            </Typography>
                        </Paper>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>

            {/* Inline confirmation for "delete this step". A second
                dialog keeps the destructive action one click away and
                explicit — the user has to read what's about to be
                removed and click Delete again. The change persists in
                place to the workflow's steps_json (no version bump). */}
            <Dialog
                open={pendingDelete != null}
                onClose={() => !deleting && setPendingDelete(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete this step?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        This permanently removes the step from the workflow's
                        saved definition. Future runs will skip it.
                    </Typography>
                    {pendingDelete && (
                        <Paper variant="outlined" sx={{ p: 1.25 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Chip size="small"
                                      label={`#${(pendingDelete.step.step_idx ?? pendingDelete.index) + 1}`}
                                      sx={{ minWidth: 44, fontWeight: 700 }} />
                                {STEP_ACTION_ICONS[pendingDelete.step.action] || null}
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {STEP_ACTION_LABELS[pendingDelete.step.action]
                                        || pendingDelete.step.action}
                                </Typography>
                            </Stack>
                            {(pendingDelete.step.selector?.primary
                              || pendingDelete.step.selector_used) && (
                                <Typography variant="caption" sx={{
                                    display: 'block',
                                    mt: 0.5,
                                    fontFamily: 'monospace',
                                    color: RPA_COLORS.textMuted,
                                    wordBreak: 'break-all',
                                }}>
                                    {pendingDelete.step.selector?.primary
                                     || pendingDelete.step.selector_used}
                                </Typography>
                            )}
                            {pendingDelete.step.semantic_label && (
                                <Typography variant="caption" sx={{
                                    display: 'block',
                                    color: RPA_COLORS.textMuted,
                                }}>
                                    {pendingDelete.step.semantic_label}
                                </Typography>
                            )}
                        </Paper>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingDelete(null)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        startIcon={deleting
                            ? <CircularProgress size={14} />
                            : <DeleteIcon fontSize="small" />}
                    >
                        {deleting ? 'Deleting…' : 'Delete step'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}
